import { useState } from 'react';

import { convertToRaw, EditorState } from 'draft-js';

import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextEditor, useEditorState } from '@components/text-editor';
import { placeholdersToBrackets } from '@components/text-editor/plugin-placeholder';
import { TextField } from '@components/text-field';
import { draftToHtml, isSameEditorState } from '@utils/draftjs';

import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { Modal, ModalProps } from 'features/modals';
import { usePlaceholderMap } from 'hooks/use-placeholder-map';
import { useStore } from 'models/Provider';

import { useCreateCompletionTemplate, useUpdateCompletionTemplate } from './api/hooks';
import { CompletionTemplate, CompletionTemplateTypes } from './api/requests';

type CompletionTemplateProps = ModalProps<{
  setEmailEnabled: (enabled: boolean) => void;
  template: CompletionTemplate | null;
  template_type: CompletionTemplateTypes;
}>;

export const CompletionTemplateModal = (props: CompletionTemplateProps) => {
  const { closeModal, setEmailEnabled, template, template_type } = props;
  const { t } = useStore();
  const { editorState, resetEditorState, setEditorState, isEmpty } = useEditorState(
    template?.contentHtml ?? '',
  );
  const [emailSubject, setEmailSubject] = useState(template?.subject ?? '');
  const [hasClickedSave, setHasClickedSave] = useState(false);

  const isEdit = template !== null;
  const isApplicantEmailTemplate = template_type === 'integrations_rookies_completion_applicant';

  const modalTitle = isEdit
    ? isApplicantEmailTemplate
      ? t('integrations.onboardingform.settings.completion-email.applicant.modal.title.edit')
      : t('integrations.onboardingform.settings.completion-email.modal.title.edit')
    : isApplicantEmailTemplate
      ? t('integrations.onboardingform.settings.completion-email.applicant.modal.title')
      : t('integrations.onboardingform.settings.completion-email.modal.title');

  const modalDescription = isApplicantEmailTemplate
    ? t('integrations.onboardingform.settings.completion-email.applicant.modal.description')
    : t('integrations.onboardingform.settings.completion-email.modal.description');

  function updateEditorState(newState: EditorState) {
    setEditorState(newState);
  }
  const placeholderMap = usePlaceholderMap(
    isApplicantEmailTemplate ? 'completion_email_applicant' : 'completion_email_admin',
  );
  const emailSubjectEmpty = emailSubject === '';

  const onCreateSuccess = () => {
    setEmailEnabled(true);
    closeModal();
  };

  const createTemplate = useCreateCompletionTemplate(onCreateSuccess);
  const updateTemplate = useUpdateCompletionTemplate(closeModal);

  const isSaveDisabled = template
    ? isSameEditorState(
        template.contentHtml,
        draftToHtml(placeholdersToBrackets(convertToRaw(editorState.getCurrentContent()))),
      ) && emailSubject === template.subject
    : false;

  const handleSaveTemplate = () => {
    setHasClickedSave(true);
    if (isEmpty || emailSubjectEmpty) {
      return;
    }
    const rawData = convertToRaw(editorState.getCurrentContent());
    const converted = placeholdersToBrackets(rawData);
    if (isEdit) {
      updateTemplate.mutate({
        templateId: template.id,
        subject: emailSubject,
        content: draftToHtml(converted),
      });
    } else {
      createTemplate.mutate({
        type: template_type,
        subject: emailSubject,
        content: draftToHtml(converted),
      });
    }
  };

  return (
    <Stack space={4}>
      <Stack orientation="horizontal" space={2} justify="space-between">
        <Modal.Header text={modalTitle} />
        <IconButton variant="ghost-purple" onClick={closeModal} icon={<IconCloseRegular />} />
      </Stack>
      <Text font="regular16" text={modalDescription} />
      <Separator />
      <Stack space={4}>
        <TextField
          label={t('integrations.onboardingform.settings.completion-email.modal.emailsubject')}
          placeholder={t('company.templates.email.subject.placeholder')}
          value={emailSubject}
          onChange={setEmailSubject}
          isDisabled={false}
          error={
            hasClickedSave &&
            emailSubjectEmpty &&
            t('integrations.onboardingform.settings.completion-email.modal.emailsubject.error')
          }
        />
        <Stack space={1}>
          <FormLabel
            text={t('integrations.onboardingform.settings.completion-email.modal.emailbody')}
          />
          <TextEditor
            variant="gray"
            disabled={false}
            editorState={editorState}
            onChange={updateEditorState}
            error={hasClickedSave && isEmpty}
          >
            <Stack orientation="horizontal" space={2} justify="space-between" align="center">
              <TextEditorToolbar />
              <TextEditor.PlaceholderMenu
                label={t('editor.placeholder.title')}
                placeholderMap={placeholderMap}
              />
            </Stack>
            <TextEditor.Input placeholder={t('company.templates.email.message.placeholder')} />
          </TextEditor>
          {hasClickedSave && isEmpty && (
            <Text
              font="regular14"
              color="error"
              text={t(
                'integrations.onboardingform.settings.completion-email.modal.emailbody.error',
              )}
            />
          )}
        </Stack>
      </Stack>
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={() => {
            closeModal();
            resetEditorState();
          }}
          text={t('integrations.onboardingform.sms.modal.cancel')}
        />
        <Button
          variant="primary-blue"
          text={t('integrations.onboardingform.sms.modal.save')}
          disabled={isSaveDisabled}
          onClick={() => handleSaveTemplate()}
        />
      </Modal.Actions>
    </Stack>
  );
};
